import type { ConsumedDrink } from '../../types';

interface DrinkHistoryProps {
  drinks: ConsumedDrink[];
  onRemoveDrink: (drinkId: string) => void;
}

export default function DrinkHistory({ drinks, onRemoveDrink }: DrinkHistoryProps) {
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('cs-CZ', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressColor = (progress: number) => {
    if (progress < 50) return 'bg-yellow-500';
    if (progress < 95) return 'bg-green-500';
    return 'bg-green-600';
  };

  if (drinks.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        Zatím žádné nápoje
      </div>
    );
  }

  // Show drinks in reverse order (most recent first)
  const sortedDrinks = [...drinks].reverse();

  return (
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {sortedDrinks.map((drink) => {
        const progress = drink.absorptionProgress ?? 0;
        const isAbsorbing = progress < 100;

        return (
          <div
            key={drink.id}
            className="p-3 bg-gray-700/50 rounded-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="font-medium text-sm">{drink.name}</div>
                <div className="text-xs text-gray-400">
                  {formatTime(drink.consumedAt)} • {drink.alcoholGrams.toFixed(1)}g alkoholu
                </div>
              </div>
              {isAbsorbing && (
                <button
                  onClick={() => onRemoveDrink(drink.id)}
                  className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                  aria-label="Odebrat nápoj"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Absorption progress bar - only show while absorbing */}
            {isAbsorbing && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-400">Vstřebává se...</span>
                  <span className="text-yellow-400">{progress}%</span>
                </div>
                <div className="h-1.5 bg-gray-600 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${getProgressColor(progress)}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
